import React, { useMemo, useState } from 'react';
import { Check, ChevronDown, MapPin, Search } from 'lucide-react';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './ui/command';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';

/**
 * Searchable location picker for the homepage hero. Groups areas under their
 * county; lets the user type to filter or pick from the list. Also accepts
 * free-text input (any string typed in the search box can be confirmed) so we
 * don't accidentally lose people whose neighbourhood isn't in our dataset yet.
 *
 * @param {Object[]} counties        Output of /api/properties/areas → counties[]
 * @param {string}   value           Currently selected display label
 * @param {Function} onChange        Called with the new label string
 * @param {string}   placeholder     Empty-state text
 */
const LocationCombobox = ({
  counties = [],
  value,
  onChange,
  placeholder = 'Where would you love to live?',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Flatten options: each county once + each area once, with a county tag.
  const options = useMemo(() => {
    const out = [];
    counties.forEach((c) => {
      if (!c?.county) return;
      out.push({
        kind: 'county',
        label: c.county,
        county: c.county,
        count: c.total ?? 0,
      });
      (c.areas || []).forEach((a) => {
        if (!a?.area) return;
        out.push({
          kind: 'area',
          label: a.area,
          county: c.county,
          count: a.count ?? 0,
        });
      });
    });
    return out;
  }, [counties]);

  const select = (label) => {
    onChange(label);
    setOpen(false);
    setQuery('');
  };

  // Fire-and-forget AI narrative pre-warm so the area page is cached + instant
  // by the time the visitor lands there. Slugify lightly — backend does
  // case-insensitive matching, so lowercase + hyphenate is enough.
  const slugify = (s) =>
    (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const prewarmNarrative = (county, area) => {
    const cs = slugify(county);
    if (!cs) return;
    const url = new URL(
      `${process.env.REACT_APP_BACKEND_URL}/api/properties/area-content`,
    );
    url.searchParams.set('county_slug', cs);
    if (area) url.searchParams.set('area_slug', slugify(area));
    url.searchParams.set('type', 'sale');
    // Best-effort — no await, no error handler. The user shouldn't wait or care.
    try {
      fetch(url.toString(), { method: 'GET', keepalive: true }).catch(() => {});
    } catch {
      /* never block the picker */
    }
  };

  const pickCounty = (county) => {
    prewarmNarrative(county, null);
    select(county);
  };
  const pickArea = (county, area) => {
    prewarmNarrative(county, area);
    select(area);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="h-12 w-full justify-between rounded-xl border-gray-200 bg-gray-50/60 hover:bg-gray-50 font-normal text-left px-3"
          data-testid="hero-location-combobox-trigger"
        >
          <span className="flex items-center gap-2 min-w-0">
            <MapPin className="h-4 w-4 text-[#7042cc] shrink-0" />
            <span className={`truncate ${value ? 'text-gray-900' : 'text-gray-500'}`}>
              {value || placeholder}
            </span>
          </span>
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0 border-gray-200"
        align="start"
        sideOffset={6}
      >
        <Command className="rounded-lg" shouldFilter={true}>
          <CommandInput
            placeholder="Type an area or county…"
            value={query}
            onValueChange={setQuery}
            data-testid="hero-location-search-input"
          />
          <CommandList className="max-h-72">
            <CommandEmpty>
              <div className="py-3 px-3 text-left">
                <div className="text-sm text-gray-700 mb-2">
                  No matches in our current dataset.
                </div>
                {query.trim() && (
                  <Button
                    type="button"
                    size="sm"
                    className="bg-[#7042cc] hover:bg-[#5b35a8] text-white"
                    onClick={() => select(query.trim())}
                    data-testid="hero-location-use-custom-btn"
                  >
                    <Search className="h-3.5 w-3.5 mr-1.5" /> Search for "{query.trim()}"
                  </Button>
                )}
              </div>
            </CommandEmpty>

            {counties.map((c) => (
              <CommandGroup
                key={c.county}
                heading={
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
                    {c.county} · {c.total ?? 0} listings
                  </span>
                }
              >
                {/* County-level pick */}
                <CommandItem
                  value={`${c.county} county all`}
                  onSelect={() => pickCounty(c.county)}
                  className="text-sm"
                  data-testid={`hero-location-county-${(c.county || '').toLowerCase()}`}
                >
                  <Check
                    className={`mr-2 h-4 w-4 ${
                      value === c.county ? 'opacity-100 text-[#7042cc]' : 'opacity-0'
                    }`}
                  />
                  <span className="font-medium">All of {c.county}</span>
                  <span className="ml-auto text-xs text-gray-400">{c.total ?? 0}</span>
                </CommandItem>
                {(c.areas || []).slice(0, 30).map((a) => (
                  <CommandItem
                    key={`${c.county}-${a.area}`}
                    // The search filter uses `value`, so we include both names
                    // for fuzzy matching.
                    value={`${a.area} ${c.county}`}
                    onSelect={() => pickArea(c.county, a.area)}
                    className="text-sm"
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        value === a.area ? 'opacity-100 text-[#7042cc]' : 'opacity-0'
                      }`}
                    />
                    {a.area}
                    <span className="ml-auto text-xs text-gray-400">{a.count ?? 0}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}

            {/* Use-typed-text option (always available when there's input) */}
            {query.trim() &&
              !options.some(
                (o) => o.label.toLowerCase() === query.trim().toLowerCase(),
              ) && (
                <CommandGroup heading="Use what you typed">
                  <CommandItem
                    value={`__custom__ ${query}`}
                    onSelect={() => select(query.trim())}
                    className="text-sm text-[#5b35a8]"
                    data-testid="hero-location-use-typed"
                  >
                    <Search className="mr-2 h-4 w-4" />
                    Search for "{query.trim()}"
                  </CommandItem>
                </CommandGroup>
              )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default LocationCombobox;
